import React, { useState, useCallback, useMemo } from 'react';
import { Card, Button, Table, Pagination } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import {
  getTableHeaderClass,
  getModernDataTableClass,
  getModernCardHeaderClass,
  getModernCardBodyClass,
  getModernCardFooterClass,
  getModernTableClass,
  getModernDataTableMetaClass,
} from 'utils/tableTheme';

const Results = ({ user, data, currentTheme, onRemoveButton }) => {
  const history = useHistory();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(40);
  const [orderBy, setOrderBy] = useState(null);
  const [orderDirection, setOrderDirection] = useState('asc');

  const handleChangePage = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  }, []);

  const handleSort = useCallback((column) => {
    const isAsc = orderBy === column && orderDirection === 'asc';
    setOrderDirection(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
    setPage(0);
  }, [orderBy, orderDirection]);

  const getSortIcon = (column) => {
    if (orderBy !== column) return <FaSort className="ms-1" />;
    return orderDirection === 'asc' ?
      <FaSortUp className="ms-1" /> :
      <FaSortDown className="ms-1" />;
  };

  const sortedData = useMemo(() => {
    if (!orderBy) return data;

    return [...data].sort((a, b) => {
      // Lógica de ordenação específica para cada coluna
      let valueA, valueB;

      switch (orderBy) {
        case 'serial':
          valueA = a.serial || '';
          valueB = b.serial || '';
          break;
        case 'ip':
          valueA = a.ip || '';
          valueB = b.ip || '';
          break;
        case 'company_id':
          valueA = a.company_id || '';
          valueB = b.company_id || '';
          break;
        case 'createdAt':
          valueA = new Date(a.createdAt || 0).getTime();
          valueB = new Date(b.createdAt || 0).getTime();
          break;
        case 'updatedAt':
          valueA = new Date(a.updatedAt || 0).getTime();
          valueB = new Date(b.updatedAt || 0).getTime();
          break;
        default:
          valueA = a[orderBy] || '';
          valueB = b[orderBy] || '';
      }

      if (valueA < valueB) {
        return orderDirection === 'asc' ? -1 : 1;
      }
      if (valueA > valueB) {
        return orderDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [data, orderBy, orderDirection]);

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const paginatedData = sortedData.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const getPaginationRange = () => {
    const start = Math.max(page - 2, 0);
    const end = Math.min(page + 2, totalPages - 1);
    let range = [];

    for (let i = start; i <= end; i++) {
      range.push(i);
    }
    return range;
  };

  const handleRowClick = (serial) => {
    history.push(`/server-local/${serial}`);
  };

  return (
    <div>
      <p className={getModernDataTableMetaClass()}>
        {data.length} registros encontrados. Página {page + 1} de {totalPages}
      </p>
      <Card
        className={getModernDataTableClass(currentTheme)}
      >
        <Card.Header
          className={getModernCardHeaderClass(currentTheme)}
        >
          Servidores Locais
        </Card.Header>
        <Card.Body
          className={getModernCardBodyClass(currentTheme)}
        >
          <Table
            style={{ fontSize: "0.9rem" }}
            hover
            responsive
            size="sm"
            className={getModernTableClass(currentTheme)}
          >
            <thead>
              <tr className={getTableHeaderClass(currentTheme)}>
                <th className='text-center' onClick={() => handleSort('serial')} style={{ cursor: 'pointer' }}>
                  SERIAL {getSortIcon('serial')}
                </th>
                <th className='text-center' onClick={() => handleSort('ip')} style={{ cursor: 'pointer' }}>
                  IP {getSortIcon('ip')}
                </th>
                <th className='text-center' onClick={() => handleSort('company_id')} style={{ cursor: 'pointer' }}>
                  EMPRESA {getSortIcon('company_id')}
                </th>
                <th className='text-center' onClick={() => handleSort('createdAt')} style={{ cursor: 'pointer' }}>
                  CRIADO EM {getSortIcon('createdAt')}
                </th>
                <th className='text-center' onClick={() => handleSort('updatedAt')} style={{ cursor: 'pointer' }}>
                  ATUALIZADO EM {getSortIcon('updatedAt')}
                </th>
                <th className='text-center'>AÇÃO</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item) => (
                <tr
                  key={item._id}
                  className="clickable-row"
                  onClick={() => user?.permission?.equipments?.read && handleRowClick(item.serial)}
                  style={{ cursor: user?.permission?.equipments?.read ? 'pointer' : 'default' }}
                >
                  <td className='text-center pt-2'>{item.serial || '--'}</td>
                  <td className='text-center pt-2'>{item.ip || '--'}</td>
                  <td className='text-center pt-2'>{item.company?.name || item.company_id || '--'}</td>
                  <td className='text-center pt-2'>
                    {new Date(item.createdAt).toLocaleString('pt-BR') || '--'}
                  </td>
                  <td className='text-center pt-2'>
                    {new Date(item.updatedAt).toLocaleString('pt-BR') || '--'}
                  </td>
                  <td className='text-center'>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveButton(item.serial);
                      }}
                      disabled={!user?.master}
                    >
                      Remover
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
        <Card.Footer
          className={getModernCardFooterClass(currentTheme)}
        >
          <div>
            <label>
              Linhas por página:{' '}
              <select className="modern-data-table__rows-select"
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}>
                {[20, 40, 60].map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <Pagination className="custom-pagination" variant="dark">
            <Pagination.Prev
              onClick={() => handleChangePage(page - 1)}
              disabled={page === 0}
              aria-label="Página anterior"

            />
            {getPaginationRange().map((index) => (
              <Pagination.Item
                key={index}
                active={index === page}
                onClick={() => handleChangePage(index)}
              >
                {index + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next
              onClick={() => handleChangePage(page + 1)}
              disabled={page >= totalPages - 1}
              aria-label="Próxima página"

            />
          </Pagination>
        </Card.Footer>
      </Card>
    </div>
  );
};

export default Results;
